import { Box, LinearProgress, Typography, Link, Button } from '@mui/material';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isAssetTypeEditorEnabled } from 'utility/functions';
import {
  checkCreateAssetsPermission,
  checkSiteAdminPermission,
  PermissionInfo,
} from 'utility/permissions';
import { bloxValues } from 'themes/constants';

import {
  importDropzoneSx,
  importFilesSx,
  importGroupContainerSx,
  importGroupHeaderSx,
  importHeaderSx,
  importProgressSx,
} from './components/ImportFieldInputStyles';
import ImportFieldSingleUpload from './components/ImportFieldSingleUpload';
import { useNxtImport, NxtImportItem } from './NxtImportContext';
import { BatchImportUploadPresets } from './components/BatchImportUploadPresets';

interface NxtImportProps {
  handleClose: () => void;
}

type DisplaySegment =
  | { kind: 'standalone'; item: NxtImportItem }
  | {
      kind: 'group';
      groupId: string;
      items: NxtImportItem[];
      timestamp: number;
      isPendingBatch: boolean;
    };

export const NxtImport = memo(({ handleClose }: NxtImportProps) => {
  const { t } = useTranslation();
  const { activeSite, sessionConfig } = useCosmos();
  const navigate = useNavigate();
  const {
    isDragActive,
    imports,
    getRootProps,
    getInputProps,
    cancelImport,
    clearAllImports,
    hasActiveImports,
  } = useNxtImport();

  /**
   * Note on permissions:
   * Since this area is used for both batch upload and wire syndication,
   * and they come in as separate permissions, we probably should not
   * outright disable the batch area button, but we can disable the file
   * upload area if the user does not have editorial create permissions...
   */
  const { granted: userIsAdmin } = checkSiteAdminPermission(sessionConfig);
  const { granted: userCanCreate, name: createPermName } =
    checkCreateAssetsPermission(sessionConfig);

  // Check if any imports are currently uploading or queued for cancel button
  const cancelDisabled = !Object.values(imports).some(
    (importItem) => importItem.isUploading || importItem.isQueued,
  );

  // Clear is disabled when there are no active imports
  const clearDisabled = hasActiveImports;

  // hide input dropzone if uploads (create perm) disabled
  const permStates: PermissionInfo = useMemo(() => {
    if (userIsAdmin) {
      return {
        info: undefined,
        disabled: false,
      };
    }
    if (!userCanCreate) {
      const batchImport = t('blox.editorial.batchImport.label');
      const reqPerms = t('blox.permissions.msg.required_short', { name: createPermName });
      // combine so reads "Batch upload requires batch upload permission"
      const batchImportNoPerms = `${batchImport} ${reqPerms.toLowerCase()}`;
      return {
        info: batchImportNoPerms,
        disabled: true,
      };
    }
    return {
      info: undefined,
      disabled: false,
    };
  }, [t, userCanCreate, createPermName, userIsAdmin]);

  const handleCancelImport = useCallback(
    (importItem: NxtImportItem) => {
      cancelImport(importItem.importId);
    },
    [cancelImport],
  );

  const handleCancelUploads = useCallback(() => {
    // Cancel all uploads that are currently uploading or queued
    Object.values(imports).forEach((importItem) => {
      if (importItem.isUploading || importItem.isQueued) {
        handleCancelImport(importItem);
      }
    });
  }, [imports, handleCancelImport]);

  const handleClearQueue = useCallback(() => {
    clearAllImports();
  }, [clearAllImports]);

  const handleNavigateToEditor = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>, uuid: string) => {
      event.preventDefault();
      const editorUrl = `/${activeSite}/editorial/edit/${uuid}`;
      navigate(editorUrl);
      handleClose();
    },
    [activeSite, navigate, handleClose],
  );

  const dzRootProps = useMemo(() => {
    return getRootProps();
  }, [getRootProps]);

  const dzInputProps = useMemo(() => {
    return getInputProps();
  }, [getInputProps]);

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const handleGroupToggle = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  // Derive display segments: standalone items and grouped items sorted newest-first.
  const displaySegments = useMemo((): DisplaySegment[] => {
    const groupMap = new Map<string, NxtImportItem[]>();
    const standaloneItems: NxtImportItem[] = [];

    Object.values(imports).forEach((item) => {
      if (item.groupId) {
        const group = groupMap.get(item.groupId) ?? [];
        group.push(item);
        groupMap.set(item.groupId, group);
      } else {
        standaloneItems.push(item);
      }
    });

    const segments: DisplaySegment[] = [
      ...standaloneItems.map((item): DisplaySegment => ({ kind: 'standalone', item })),
      ...Array.from(groupMap.entries()).map(
        ([groupId, groupItems]): DisplaySegment => ({
          kind: 'group',
          groupId,
          items: groupItems,
          // Use the maximum timestamp so the group sorts by its most-recent member.
          timestamp: Math.max(...groupItems.map((i) => i.timestamp)),
          isPendingBatch: groupItems.every((i) => i.isPendingBatch === true),
        }),
      ),
    ];

    return segments.sort((a, b) => {
      const tsA = a.kind === 'standalone' ? a.item.timestamp : a.timestamp;
      const tsB = b.kind === 'standalone' ? b.item.timestamp : b.timestamp;
      return tsB - tsA;
    });
  }, [imports]);

  const strDropzone = t('blox.actions.dropzone');
  const strCancelUploads = t('blox.actions.cancel_uploads');
  const strClearQueue = t('blox.actions.clear_queue');

  return (
    <Box data-testid="batch-import-container">
      <BatchImportUploadPresets />

      <Box
        data-testid="batch-import-dropzone-area"
        sx={importHeaderSx}
      >
        {!permStates.disabled ? (
          <Box
            data-testid="batch-import-dropzone"
            sx={(theme) => importDropzoneSx(theme, isDragActive)}
            {...dzRootProps}
          >
            <input
              data-testid="batch-import-dropzone-input"
              {...dzInputProps}
            />
            <FileCopyIcon sx={{ fontSize: 32 }} />
            <Typography variant="overline">{strDropzone}</Typography>
          </Box>
        ) : (
          <Typography
            data-testid="batch-import-dropzone-disabled"
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              border: `1px solid ${theme.palette.action.hover}`,
              borderRadius: 2,
              bgcolor: theme.palette.action.hover,
              fontSize: bloxValues.tinyFontSizeRem,
              color: theme.palette.text.disabled,
            })}
          >
            <InfoRoundedIcon
              sx={(theme) => ({
                fontSize: 20,
                color: theme.palette.text.disabled,
              })}
            />
            {permStates?.info}
          </Typography>
        )}

        <Box
          data-testid="batch-import-actions"
          aria-label="batch-import-actions"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {!permStates.disabled && (
            <Button
              data-testid="batch-import-cancel-button"
              size="small"
              onClick={handleCancelUploads}
              disabled={cancelDisabled}
            >
              {strCancelUploads}
            </Button>
          )}

          <Button
            data-testid="batch-import-clear-button"
            size="small"
            onClick={handleClearQueue}
            disabled={clearDisabled}
          >
            {strClearQueue}
          </Button>
        </Box>
      </Box>

      {/* Headers/labels for the segments of the batch upload items. */}
      <Box
        data-testid="batch-upload-publish-item-labels"
        aria-label="batch-upload-publish-item-labels"
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr auto 1fr',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          data-testid="batch-upload-publish-filename-label"
          aria-label="batch-upload-publish-filename-label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 1,
            px: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('blox.batch_import.columns.file_name', 'File name')}
          </Typography>
        </Box>
        <Box
          data-testid="batch-upload-publish-status-label"
          aria-label="batch-upload-publish-status-label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 120,
            gap: 1,
            px: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('blox.editorial.asset.properties.no_publish.label_alt', 'Publish status')}
          </Typography>
        </Box>
        <Box
          data-testid="batch-upload-publish-upload-label"
          aria-label="batch-upload-publish-upload-label"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1,
            px: 1,
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('blox.batch_import.columns.upload_status', 'Upload status')}
          </Typography>
        </Box>
      </Box>

      {/* Imported Items Overview */}
      <Box
        data-testid="batch-import-file-progress"
        id="fileProgress"
        sx={importFilesSx}
      >
        {displaySegments.map((segment) => {
          if (segment.kind === 'standalone') {
            const importItem = segment.item;
            const hasUuid = importItem?.uuid;
            const editorEnabled = isAssetTypeEditorEnabled(importItem?.type);
            if (hasUuid && editorEnabled) {
              return (
                <Link
                  key={`batch-import-linkable-${importItem.importId}`}
                  id="card-link-wrapper-link-enabled"
                  data-testid="batch-import-item-link"
                  href={`/${activeSite}/editorial/edit/${importItem?.uuid}`}
                  onClick={(event) =>
                    importItem?.uuid && handleNavigateToEditor(event, importItem.uuid)
                  }
                >
                  <ImportFieldSingleUpload
                    item={importItem}
                    onCancelClick={(e) => {
                      e?.stopPropagation();
                      handleCancelImport(importItem);
                    }}
                    edit
                  />
                </Link>
              );
            }
            return (
              <ImportFieldSingleUpload
                key={`batch-import-${importItem.importId}`}
                item={importItem}
                onCancelClick={(e) => {
                  e?.stopPropagation();
                  handleCancelImport(importItem);
                }}
              />
            );
          }

          // Group segment
          const isGroupActive = segment.items.some(
            (item) => item.isUploading || item.isQueued || item.isProcessing,
          );
          const isGroupAllDone = segment.items.every(
            (item) => item.isReady || item.isFailed || item.isCanceled,
          );
          const hasGroupErrors = segment.items.some((item) => item.isFailed);

          return (
            <Accordion
              expanded={expandedGroups.has(segment.groupId)}
              onChange={() => handleGroupToggle(segment.groupId)}
              key={`batch-import-group-${segment.groupId}`}
              data-testid="batch-import-group"
              sx={importGroupContainerSx}
            >
              <AccordionSummary
                sx={(theme) => ({
                  ...importGroupHeaderSx(theme),
                  ...(isGroupActive && { height: 'auto' }),
                })}
                expandIcon={<ExpandMoreIcon />}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',

                    width: '100%',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      pr: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="overline">
                        {t('blox.batch_import.group_label', {
                          count: segment.items.length,
                        })}
                      </Typography>
                      <Button
                        data-testid="batch-import-group-search-button"
                        size="small"
                        href={`${window.location.origin}/${activeSite}/editorial/search?sort=start_time&dir=DESC&query=tag_keyword%3A"%23nxt-import-group-${segment.groupId}"`}
                        target="_blank"
                        sx={{
                          minWidth: 'auto',
                          padding: 0,
                        }}
                        onClick={(e) => {
                          // prevent accordion toggling
                          e.stopPropagation();
                        }}
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </Button>
                    </Box>
                    {/* Pending chip — unchanged; only shown when no active upload */}
                    {segment.isPendingBatch && !isGroupActive && (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          color: theme.palette.info.main,
                          ml: 1,
                          textTransform: 'capitalize',
                        })}
                      >
                        {t('blox.batch_import.status.pending', 'Pending')}
                      </Typography>
                    )}

                    {/* Success status */}
                    {isGroupAllDone && !hasGroupErrors && (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          color: theme.palette.success.main,
                          ml: 1,
                          textTransform: 'capitalize',
                        })}
                      >
                        {t('blox.batch_import.status.success', 'Success')}
                      </Typography>
                    )}

                    {/* Finished with problems status */}
                    {isGroupAllDone && hasGroupErrors && (
                      <Typography
                        variant="caption"
                        sx={(theme) => ({
                          color: theme.palette.error.main,
                          ml: 1,
                        })}
                      >
                        {t(
                          'blox.batch_import.status.finished_with_problems',
                          'Finished with problems',
                        )}
                      </Typography>
                    )}
                  </Box>

                  {/* Indeterminate progress bar — visible even when accordion is collapsed */}
                  {isGroupActive && (
                    <LinearProgress
                      data-testid="batch-import-group-progress"
                      variant="indeterminate"
                      sx={importProgressSx}
                    />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails
                sx={{ p: 1, gap: 1.5, display: 'flex', flexDirection: 'column' }}
              >
                {segment.items.map((importItem) => {
                  const hasUuid = importItem?.uuid;
                  const editorEnabled = isAssetTypeEditorEnabled(importItem?.type);
                  if (hasUuid && editorEnabled) {
                    return (
                      <Link
                        key={`batch-import-linkable-${importItem.importId}`}
                        id="card-link-wrapper-link-enabled"
                        data-testid="batch-import-item-link"
                        href={`/${activeSite}/editorial/edit/${importItem?.uuid}`}
                        onClick={(event) =>
                          importItem?.uuid &&
                          handleNavigateToEditor(event, importItem.uuid)
                        }
                      >
                        <ImportFieldSingleUpload
                          item={importItem}
                          onCancelClick={(e) => {
                            e?.stopPropagation();
                            handleCancelImport(importItem);
                          }}
                          edit
                          isNested
                        />
                      </Link>
                    );
                  }
                  return (
                    <ImportFieldSingleUpload
                      key={`batch-import-${importItem.importId}`}
                      item={importItem}
                      onCancelClick={(e) => {
                        e?.stopPropagation();
                        handleCancelImport(importItem);
                      }}
                      isNested
                    />
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
});

NxtImport.displayName = 'NxtImport';
export default NxtImport;
